/**
 * Sistema Centralizado de Mensagens
 *
 * Todas as mensagens do sistema em um único lugar para:
 * - Fácil manutenção
 * - Consistência
 * - Preparação para i18n
 */

import toast from 'react-hot-toast';

// ============================================================================
// FUNÇÕES DE NOTIFICAÇÃO (TOAST)
// ============================================================================

/**
 * Atalhos para mostrar toasts elegantes
 * Substitui alert() nativo que mostrava "localhost says"
 */
export const msg = {
  success: (message: string) => {
    toast.success(message, {
      duration: 4000,
      style: {
        background: '#10B981',
        color: '#fff',
        fontWeight: 500,
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#10B981',
      },
    });
  },

  error: (message: string) => {
    toast.error(message, {
      duration: 6000,
      style: {
        background: '#EF4444',
        color: '#fff',
        fontWeight: 500,
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#EF4444',
      },
    });
  },

  warning: (message: string) => {
    toast(message, {
      duration: 5000,
      icon: '⚠️',
      style: {
        background: '#F59E0B',
        color: '#fff',
        fontWeight: 500,
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  },

  info: (message: string) => {
    toast(message, {
      duration: 4000,
      icon: 'ℹ️',
      style: {
        background: '#3B82F6',
        color: '#fff',
        fontWeight: 500,
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  },

  loading: (message: string) => {
    return toast.loading(message, {
      style: {
        background: '#6B7280',
        color: '#fff',
        fontWeight: 500,
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    });
  },

  dismiss: (toastId?: string) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },
};

// ============================================================================
// MENSAGENS CENTRALIZADAS - ORGANIZADAS POR DOMÍNIO
// ============================================================================

export const MESSAGES = {
  // --------------------------------------------------------------------------
  // GENÉRICAS
  // --------------------------------------------------------------------------
  GENERIC: {
    SUCCESS: 'Operação realizada com sucesso!',
    ERROR: 'Erro ao realizar operação',
    LOADING: 'Carregando...',
    SAVING: 'Salvando...',
    DELETING: 'Excluindo...',
    NO_DATA: 'Nenhum dado encontrado',
    REQUIRED_FIELDS: 'Preencha todos os campos obrigatórios',
    NO_CHANGES: 'Nenhuma alteração foi feita',
    NOT_IMPLEMENTED: 'Funcionalidade em desenvolvimento',
  },

  // --------------------------------------------------------------------------
  // VALIDAÇÃO
  // --------------------------------------------------------------------------
  VALIDATION: {
    REQUIRED: 'Campo obrigatório',
    INVALID_EMAIL: 'Email inválido. Por favor, insira um email válido com @',
    INVALID_CPF: 'CPF inválido. Por favor, verifique os dígitos',
    INVALID_CNPJ: 'CNPJ inválido. Por favor, verifique os dígitos',
    INVALID_CPF_CNPJ_LENGTH: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos',
    CPF_CNPJ_LENGTH: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos',
    INVALID_GTIN: 'GTIN deve ter 8, 12, 13 ou 14 dígitos numéricos',
    INVALID_FILE_TYPE: 'Tipo de arquivo inválido',
    FILE_TOO_LARGE: 'Arquivo muito grande',
    SELECT_FILE: 'Selecione um arquivo',
    SELECT_ITEM: 'Selecione um item',
  },

  // --------------------------------------------------------------------------
  // USUÁRIOS
  // --------------------------------------------------------------------------
  USER: {
    CREATED: 'Usuário criado com sucesso!',
    UPDATED: 'Usuário atualizado com sucesso!',
    DELETED: 'Usuário excluído com sucesso!',
    LOAD_ERROR: 'Erro ao carregar usuários',
    SAVE_ERROR: 'Erro ao salvar usuário',
    CREATE_ERROR: (detail: string) => `Erro ao criar usuário: ${detail}`,
    DELETE_ERROR: 'Erro ao excluir usuário',
    REFRESH_ERROR: 'Erro ao atualizar lista',
    CONFIRM_DELETE: (username: string) => `Tem certeza que deseja excluir o usuário "${username}"?`,
    CONFIRM_DELETE_MSG: 'Esta ação irá desativar o usuário no sistema.',
    CONFIRM_CANCEL: 'Deseja desfazer as alterações e restaurar os dados originais?',
    PASSWORD_COPIED: 'Senha copiada para a área de transferência!',
    LIST_REFRESHED: 'Lista atualizada com sucesso!',
    CHANGES_CANCELLED: 'Alterações canceladas',
    EXPORT_EMPTY: 'Não há usuários para exportar',
    EXPORT_SUCCESS: 'Dados exportados com sucesso!',
    EXPORT_ERROR: 'Erro ao exportar dados',
    FORM_LOAD_ERROR: 'Erro ao carregar dados do formulário',
  },

  // --------------------------------------------------------------------------
  // CEP (Consulta de Endereço)
  // --------------------------------------------------------------------------
  CEP: {
    NOT_FOUND: (cep: string) => `CEP "${cep}" não encontrado. Por favor, verifique o CEP ou preencha o endereço manualmente.`,
    SERVICE_ERROR: 'Serviço de CEP indisponível. Por favor, preencha o endereço manualmente.',
    INVALID: 'CEP inválido. O CEP deve ter 8 dígitos.',
    FOUND: 'Endereço encontrado e preenchido automaticamente!',
  },

  // --------------------------------------------------------------------------
  // HOSPITAIS/ESTABELECIMENTOS
  // --------------------------------------------------------------------------
  HOSPITAL: {
    CREATED: 'Hospital criado com sucesso!',
    UPDATED: 'Hospital atualizado com sucesso!',
    DELETED: 'Hospital excluído com sucesso!',
    LOAD_ERROR: 'Erro ao carregar hospitais',
    CREATE_ERROR: 'Erro ao criar hospital',
    SAVE_ERROR: 'Erro ao salvar alterações do hospital',
    DELETE_ERROR: 'Erro ao excluir hospital',
    // Excel exports
    EXCEL_EXPORTED: 'Arquivo Excel exportado com sucesso!',
    EXCEL_EXPORT_ERROR: 'Erro ao exportar arquivo Excel',
    LOCAIS_EXPORTED: 'Locais exportados com sucesso!',
    LOCAIS_EXPORT_ERROR: 'Erro ao exportar locais',
    LOCAIS_TEMPLATE_ERROR: 'Erro ao baixar template de locais',
    CONFIRM_DELETE: (name: string) => `Tem certeza que deseja excluir o hospital "${name}"?`,
    SELECT_HOSPITAL: 'Selecione um hospital',
    NO_SELECTION: 'Nenhum hospital selecionado',
    EXPORT_SUCCESS: 'Arquivo Excel exportado com sucesso!',
    EXPORT_ERROR: 'Erro ao exportar arquivo Excel',
    TEMPLATE_ERROR: 'Erro ao baixar template',
    SELECT_FILE: 'Selecione um arquivo Excel',
    // Locais de Contagem
    LOCAL_REQUIRED: 'Por favor, preencha os campos obrigatórios: Local da Contagem e Responsável',
    LOCAL_SAVED: 'Local de contagem salvo com sucesso!',
    LOCAL_SAVE_ERROR: 'Erro ao salvar local de contagem',
    LOCAL_DELETED: 'Local de contagem deletado com sucesso!',
    LOCAL_DELETE_ERROR: 'Erro ao deletar local de contagem',
    LOCAL_CONFIRM_DELETE: 'Tem certeza que deseja deletar este local de contagem?',
    LOCAL_EXPORT_SUCCESS: 'Locais exportados com sucesso!',
    LOCAL_EXPORT_ERROR: 'Erro ao exportar locais',
    LOCAL_TEMPLATE_ERROR: 'Erro ao baixar template de locais',
    // Representantes
    REP_EXPORT_ERROR: 'Erro ao exportar representantes',
  },

  // --------------------------------------------------------------------------
  // CLIENTES
  // --------------------------------------------------------------------------
  CLIENT: {
    DEACTIVATED: 'Cliente desativado com sucesso!',
    DEACTIVATE_ERROR: 'Erro ao desativar cliente',
    CONFIRM_DEACTIVATE: (name: string) => `Deseja realmente desativar o cliente "${name}"?`,
    LOAD_ERROR: 'Erro ao carregar clientes',
    // Produtos
    PRODUCT_CREATED: 'Produto criado com sucesso',
    PRODUCT_UPDATED: 'Produto atualizado com sucesso',
    PRODUCT_DELETED: 'Produto excluído com sucesso',
    PRODUCT_LOAD_ERROR: 'Erro ao carregar produtos do cliente',
    PRODUCT_SAVE_ERROR: 'Erro ao salvar produto',
    PRODUCT_DELETE_ERROR: 'Erro ao excluir produto',
    PRODUCT_CONFIRM_DELETE: (code: string) => `Deseja realmente excluir o produto "${code}"?`,
    PRODUCT_RESTORED: 'Produto restaurado com sucesso',
    PRODUCT_RESTORE_ERROR: 'Erro ao restaurar produto',
    PRODUCT_CONFIRM_RESTORE: (code: string) => `Deseja restaurar o produto "${code}" para ativo?`,
    PRODUCT_IMPORT_ERROR: 'Erro ao importar produtos do Excel',
    // GTINs
    GTIN_ADDED: 'GTIN adicionado com sucesso',
    GTIN_DELETED: 'GTIN excluído com sucesso',
    GTIN_LOAD_ERROR: 'Erro ao carregar GTINs do produto',
    GTIN_ADD_ERROR: 'Erro ao adicionar GTIN',
    GTIN_DELETE_ERROR: 'Erro ao excluir GTIN',
    GTIN_CONFIRM_DELETE: 'Deseja realmente excluir este GTIN?',
    GTIN_IMPORT_ERROR: 'Erro ao importar GTINs do Excel',
    // Representantes
    REP_DELETED: 'Representante excluído com sucesso',
    REP_LOAD_ERROR: 'Erro ao carregar representantes do cliente',
    REP_DELETE_ERROR: 'Erro ao excluir representante',
    REP_CONFIRM_DELETE: 'Deseja realmente excluir este representante?',
    // Hospitais
    HOSPITAL_LOAD_ERROR: 'Erro ao carregar hospitais atendidos pelo cliente',
    HOSPITAL_UNLINK_ERROR: 'Erro ao desvincular hospital',
    HOSPITAL_CONFIRM_UNLINK: 'Deseja realmente desvincular este hospital do cliente?',
    // Exportação
    EXPORT_EMPTY_PRODUCTS: 'Não há produtos para exportar',
    EXPORT_EMPTY_REPS: 'Não há representantes para exportar',
    EXPORT_EMPTY_HOSPITALS: 'Não há hospitais para exportar',
  },

  // --------------------------------------------------------------------------
  // CONTAGENS
  // --------------------------------------------------------------------------
  COUNTING: {
    CREATED: (id: number) => `Contagem criada com sucesso! ID: ${id}`,
    UPDATED: 'Contagem atualizada com sucesso!',
    DELETED: (id: number) => `Contagem ${id} deletada com sucesso!`,
    CREATE_ERROR: (error: string) => `Erro ao criar contagem: ${error}`,
    UPDATE_ERROR: 'Erro ao atualizar contagem',
    DELETE_ERROR: (error: string) => `Erro ao deletar contagem: ${error}`,
    CONFIRM_DELETE: (title: string) => `Deseja deletar a contagem "${title}"?`,
    CANNOT_DELETE_UNSAVED: 'Não é possível deletar uma contagem que ainda não foi salva.',
    // Validações
    SELECT_CLIENT: 'Por favor, selecione um cliente',
    SELECT_ESTABLISHMENT: 'Por favor, selecione um estabelecimento',
    ADD_AUDITOR: 'Por favor, adicione pelo menos um auditor',
    // Upload/Download
    SELECT_FILE_FIRST: 'Por favor, selecione um arquivo Excel primeiro.',
    SAVE_BEFORE_IMPORT: 'Salve a contagem antes de importar o Excel. Preencha Cliente, Estabelecimento, Local de Contagem e Auditor, depois clique em SALVAR.',
    UPLOAD_ERROR: (error: string) => `Erro ao fazer upload: ${error || 'Erro desconhecido'}`,
    SAVE_BEFORE_DOWNLOAD: 'Por favor, salve a contagem antes de fazer o download',
    DOWNLOAD_SUCCESS: 'Download concluído com sucesso!',
    DOWNLOAD_NO_DATA: 'Nenhum dado encontrado para download. Faça o upload de um Excel primeiro.',
    DOWNLOAD_ERROR: (error: string) => `Erro ao fazer download: ${error || 'Erro desconhecido'}`,
    REPORT_NOT_IMPLEMENTED: (type: string) => `Relatório "${type}" será implementado na FASE 4`,
    // Entry Modal
    SELECT_PRODUCT: 'Selecione um produto',
    QTY_GREATER_ZERO: 'Quantidade deve ser maior que zero',
    ENTRY_UPDATED: 'Contagem atualizada com sucesso!',
    ENTRY_ADDED: 'Contagem adicionada com sucesso!',
    ENTRY_SAVE_ERROR: 'Erro ao salvar contagem',
  },

  // --------------------------------------------------------------------------
  // REPRESENTANTES
  // --------------------------------------------------------------------------
  REPRESENTATIVE: {
    SAVED: 'Representante salvo com sucesso!',
    DELETED: 'Representante excluído com sucesso!',
    SAVE_ERROR: 'Erro ao salvar representante',
    DELETE_ERROR: 'Erro ao excluir representante',
    CONFIRM_DELETE: 'Tem certeza que deseja excluir este representante?',
    SAVED_WITH_HOSPITAL_ERROR: 'Representante salvo, mas houve erro ao vincular hospitais',
    HOSPITAL_LINK_ERROR: 'Erro ao vincular hospital ao representante',
    HOSPITAL_UNLINK_ERROR: 'Erro ao remover hospital do representante',
    HOSPITAL_CONFIRM_UNLINK: 'Deseja realmente remover este hospital do representante?',
    IMPORT_ERROR: 'Erro ao importar arquivo',
    TEMPLATE_ERROR: 'Erro ao baixar template',
    LOAD_ERROR: 'Erro ao carregar hospitais',
  },

  // --------------------------------------------------------------------------
  // CONFIGURAÇÕES
  // --------------------------------------------------------------------------
  CONFIG: {
    // Logo
    LOGO_UPDATED: 'Logo atualizado com sucesso!',
    LOGO_UPLOAD_ERROR: (error: string) => `Erro ao fazer upload do logo: ${error}`,
    LOGO_DELETED: 'Logo removido com sucesso! Logo padrão será usado.',
    LOGO_DELETE_ERROR: (error: string) => `Erro ao remover logo: ${error}`,
    LOGO_INVALID_TYPE: 'Tipo de arquivo inválido. Use PNG, JPG ou SVG.',
    LOGO_TOO_LARGE: 'Arquivo muito grande. Tamanho máximo: 5MB.',
    LOGO_CONFIRM_DELETE: 'Tem certeza que deseja remover o logo customizado?',
    // Mini Logo
    MINI_LOGO_UPDATED: 'Mini logo e favicon atualizados com sucesso!',
    MINI_LOGO_UPLOAD_ERROR: (error: string) => `Erro ao fazer upload do mini logo: ${error}`,
    MINI_LOGO_DELETED: 'Mini logo e favicon removidos com sucesso! Padrão será usado.',
    MINI_LOGO_DELETE_ERROR: (error: string) => `Erro ao remover mini logo: ${error}`,
    MINI_LOGO_INVALID_TYPE: 'Tipo de arquivo inválido. Use PNG, JPG ou SVG.',
    MINI_LOGO_TOO_LARGE: 'Arquivo muito grande. Tamanho máximo para mini logo: 1MB.',
    MINI_LOGO_CONFIRM_DELETE: 'Tem certeza que deseja remover o mini logo customizado?',
  },

  // --------------------------------------------------------------------------
  // SMTP
  // --------------------------------------------------------------------------
  SMTP: {
    TEST_SUCCESS: 'Email de teste enviado com sucesso!',
    TEST_ERROR: 'Erro ao enviar email de teste',
    CONFIG_NOT_FOUND: 'Configuração SMTP não encontrada. Configure primeiro.',
    INVALID_CREDENTIALS: 'Credenciais SMTP inválidas. Verifique email e senha.',
    CONNECTION_ERROR: 'Erro ao conectar com servidor SMTP',
    FILL_REQUIRED_FIELDS: 'Preencha email e senha SMTP',
  },

  // --------------------------------------------------------------------------
  // PRODUTOS
  // --------------------------------------------------------------------------
  PRODUCT: {
    CREATED: 'Produto criado com sucesso!',
    UPDATED: 'Produto atualizado com sucesso!',
    DELETED: 'Produto excluído com sucesso!',
    SAVE_ERROR: 'Erro ao salvar produto',
    DELETE_ERROR: 'Erro ao excluir produto',
    CONFIRM_DELETE: (name: string) => `Deseja remover o produto "${name}"?`,
    LOAD_ERROR: 'Erro ao carregar produtos',
    SELECT_PRODUCT: 'Selecione um produto primeiro',
    CONFIRM_REMOVE: (name: string) => `Deseja remover o vínculo com "${name}"?`,
    // GTINs
    GTIN_ADDED: 'GTIN adicionado com sucesso!',
    GTIN_DELETED: 'GTIN excluído com sucesso!',
    GTIN_FOUND: 'Produto encontrado!',
    GTIN_NOT_FOUND: 'Nenhum produto encontrado com este GTIN',
    GTIN_LOAD_ERROR: 'Erro ao carregar GTINs do produto',
    GTIN_ADD_ERROR: 'Erro ao adicionar GTIN',
    GTIN_DELETE_ERROR: 'Erro ao excluir GTIN',
    GTIN_CONFIRM_DELETE: 'Deseja realmente excluir este GTIN?',
    GTIN_EMPTY: 'Digite um GTIN válido',
    GTIN_SEARCH_EMPTY: 'Digite um GTIN para buscar',
    SELECT_CLIENT: 'Selecione um cliente primeiro',
    // Import
    IMPORT_INVALID_FILE: 'Por favor, selecione um arquivo Excel (.xlsx ou .xls)',
    IMPORT_SELECT_FILE: 'Selecione um arquivo primeiro',
    IMPORT_SUCCESS: (created: number, updated: number) => `${created} produtos criados, ${updated} atualizados`,
    IMPORT_GTIN_SUCCESS: (created: number, updated: number) => `${created} GTINs criados, ${updated} atualizados`,
    IMPORT_ERROR: 'Erro ao importar arquivo. Verifique o formato.',
  },

  // --------------------------------------------------------------------------
  // IMPORTAÇÃO EXCEL
  // --------------------------------------------------------------------------
  IMPORT: {
    SUCCESS: (total: number, processed: number, errors: number, rate: number) =>
      `Importação concluída! Total: ${total}, Processadas: ${processed}, Erros: ${errors}, Taxa: ${rate}%`,
    ERROR: 'Erro ao importar arquivo',
    SELECT_FILE: 'Selecione um arquivo Excel',
    INVALID_FORMAT: 'Formato de arquivo inválido',
  },

  // --------------------------------------------------------------------------
  // JOBS (LEGACY)
  // --------------------------------------------------------------------------
  JOB: {
    LOAD_ERROR: 'Erro ao carregar dados do job',
    SAVE_ERROR: 'Erro ao salvar alterações',
    DELETE_ITEM_ERROR: 'Erro ao deletar produto',
    ADD_ITEM_ERROR: 'Erro ao adicionar produto',
    SELECT_PRODUCT: 'Selecione um produto',
    CONFIRM_DELETE_PRODUCT: 'Tem certeza que deseja deletar este produto?',
    CONFIRM_DELETE_COUNT: 'Tem certeza que deseja deletar esta contagem?',
    CONFIRM_FINISH: 'Tem certeza que deseja finalizar este job?',
    CONFIRM_UPLOAD: 'Upload de Excel irá adicionar produtos a esta contagem. Continuar?',
  },
};

export default MESSAGES;
